"use client";

import { useState } from "react";

import { DeleteConfirmDialog } from "@/components/common/DeleteConfirmDialog";

/**
 * Helper hook для удаления — заменяет window.confirm() на shadcn Dialog.
 *
 * Usage:
 * const { askDelete, dialog } = useDeleteConfirm({
 *   onConfirm: async (id) => await deleteMutation.mutateAsync(id),
 * });
 *
 * // В onClick: askDelete(item.id, item.name)
 * // В JSX в конце компонента: {dialog}
 */
export function useDeleteConfirm<T = number>({
  onConfirm,
  title,
  description,
  confirmLabel = "Удалить",
}: {
  onConfirm: (id: T) => Promise<void> | void;
  title?: string;
  description?: React.ReactNode;
  confirmLabel?: string;
}) {
  const [target, setTarget] = useState<{ id: T; name?: string } | null>(null);

  function askDelete(id: T, name?: string) {
    setTarget({ id, name });
  }

  const dialog = (
    <DeleteConfirmDialog
      open={target !== null}
      onClose={() => setTarget(null)}
      onConfirm={async () => {
        if (target === null) return;
        await onConfirm(target.id);
        setTarget(null);
      }}
      title={title}
      itemName={target?.name}
      description={description}
      confirmLabel={confirmLabel}
    />
  );

  return { askDelete, dialog };
}
